<script setup lang="ts" generic="T">
  import type { Table } from '@tanstack/vue-table';
  import { computed } from 'vue';

  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { NumericText } from '@ui/components/number-field';
  import { Separator } from '@ui/components/separator';

  interface TableSelectionProps {
    globalSelection?: T[];
    table: Table<T>;
  }

  const props = defineProps<TableSelectionProps>();

  const emit = defineEmits<{
    (e: 'clearSelection'): void;
  }>();

  const resetSelection = () => {
    props.table.resetRowSelection();
    emit('clearSelection');
  };

  const numSelected = computed<number>(() => {
    return props.globalSelection
      ? props.globalSelection.length
      : props.table.getFilteredSelectedRowModel().rows.length;
  });
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <ConfirmDialog confirm-text="Clear" @confirm="resetSelection">
      <template #trigger>
        <Button
          variant="ghost"
          class="flex border border-dashed gap-2"
          size="sm"
          :disabled="!numSelected"
        >
          <NumericText :value="numSelected" class="px-1"> Selected</NumericText>
          <template v-if="numSelected">
            <Separator class="h-full" orientation="vertical" />
            <i class="fal fa-times w-fit text-xs" />
          </template>
        </Button>
      </template>

      <template #title> Clear selection?</template>

      <template #description>
        This will clear your current selection of
        {{ numSelected }}
        rows. It can't be undone.
      </template>
    </ConfirmDialog>

    <Separator v-if="$slots.actions" orientation="vertical" class="h-6" />

    <slot name="actions" />
  </div>
</template>

<style scoped></style>
